import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, TextField, Button, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import PercentIcon from '@mui/icons-material/Percent';

const TaxForm = ({ selected, onSuccess, onCancel }) => {
  const [title, setTitle] = useState('');
  const [percentage, setPercentage] = useState('0');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selected) {
      setTitle(selected.title);
      setPercentage(selected.percentage || '0');
    } else {
      setTitle('');
      setPercentage('0');
    }
  }, [selected]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = { 
        title,
        percentage: parseFloat(percentage) || 0
      };
      
      if (selected) {
        await axios.put(`/api/taxes/${selected.id}`, data);
      } else {
        await axios.post('/api/taxes', data);
      }
      onSuccess();
      setTitle('');
      setPercentage('0');
    } catch (error) {
      console.error('Error saving tax:', error);
      alert('Failed to save tax');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        bgcolor: '#43a047',
        color: 'white',
        fontSize: '1rem',
        py: 1.5
      }}>
        <PercentIcon />
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
          {selected ? 'Edit Tax' : 'Add New Tax'}
        </Typography>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box component="form" id="tax-form" onSubmit={handleSubmit} sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            fullWidth
            value={title}
            onChange={e => setTitle(e.target.value)}
            label="Tax Title"
            placeholder="e.g., VAT, GST"
            required
            autoFocus
            sx={{ 
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#ff6b6b',
                }
              }
            }}
          />

          <TextField
            fullWidth
            value={percentage}
            onChange={e => setPercentage(e.target.value)}
            label="Percentage (%)"
            placeholder="e.g., 5, 10, 18"
            type="number"
            inputProps={{ step: '0.01', min: '0' }}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#ff6b6b',
                }
              }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ py: 1, px: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', gap: 1 }}>
        <Button 
          onClick={onCancel}
          variant="outlined"
          startIcon={<CancelIcon />}
          sx={{ 
            borderColor: '#757575',
            color: '#757575',
            '&:hover': {
              borderColor: '#424242',
              bgcolor: '#f5f5f5'
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          form="tax-form"
          variant="contained" 
          disabled={loading}
          startIcon={selected ? <EditIcon /> : <AddIcon />}
          sx={{ 
            bgcolor: '#43a047',
            fontSize: '0.75rem',
            py: 0.5,
            px: 1.5,
            '&:hover': {
              bgcolor: '#388e3c'
            }
          }}
        >
          {selected ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </>
  );
};

export default TaxForm;
